import { useCallback, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { RecommendationError } from '@atlassian/cross-join-nudges';

import {
	RECOMMENDATION_PRODUCT_KEYS,
	useMSRNudgeRecommendations,
} from '../recommendations/index.tsx';
import { getFeatureVisited } from '../caching/index.tsx';
import { fireErrorAnalytics } from '../analytics/index.tsx';
import { useJpdMsrCrossJoinNudge } from '../../ui/nudges/jpd-msr-nudge.tsx';
import { useOverrides } from '../overrides/index.tsx';
import { useMsrConfluenceCrossJoinNudge } from '../../ui/nudges/confluence-msr-nudge.tsx';
import { useMsrJpdCrossJoinNudgeEligibility } from './jpd-eligibility.tsx';
import { useMsrConfluenceCrossJoinNudgeEligibility } from './confluence-eligibility.tsx';
import { useMsrSnoozeHandler } from './snooze-handler.tsx';
import { useMsrAnalytics } from './analytics.tsx';

/*
  Eligibility can be overriden using the following query parameter:

  ?cjn_msr_overrides={
    "feature_visited_too_recently": false,
  }
*/
type OverrideKey = 'feature_visited_too_recently';

export const useExecuteMSRNudge = () => {
	const sessionId = useRef(uuid());
	const cloudId = useCloudId();
	const getMSRNudgeRecommendations = useMSRNudgeRecommendations({ sessionId: sessionId.current });
	const getMsrJpdCrossJoinNudgeEligibility = useMsrJpdCrossJoinNudgeEligibility();
	const getMsrConfluenceCrossJoinNudgeEligibility = useMsrConfluenceCrossJoinNudgeEligibility();
	const { blockEligibilityUntilTomorrow, blockEligibilityToPreventRace, snooze } =
		useMsrSnoozeHandler();
	const showJpdMsrCrossJoinNudge = useJpdMsrCrossJoinNudge({ sessionId: sessionId.current });
	const showMsrConfluenceCrossJoinNudge = useMsrConfluenceCrossJoinNudge({
		sessionId: sessionId.current,
	});
	const withOverrides = useOverrides<OverrideKey>('cjn_msr_overrides');
	const { fireOnlyNewEvent } = useMsrAnalytics();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(async () => {
		try {
			if (withOverrides('feature_visited_too_recently', getFeatureVisited('crossJoinNudgesMsr'))) {
				return;
			}
			blockEligibilityToPreventRace();

			const [jpdResult, confluenceResult] = await Promise.all([
				getMsrJpdCrossJoinNudgeEligibility(),
				getMsrConfluenceCrossJoinNudgeEligibility(),
			]);

			await Promise.all([
				await fireOnlyNewEvent({
					value: confluenceResult,
					eventName: 'msrCjnEligibility checked',
					key: RECOMMENDATION_PRODUCT_KEYS.CONFLUENCE,
				}),
				await fireOnlyNewEvent({
					value: jpdResult,
					eventName: 'msrCjnEligibility checked',
					key: RECOMMENDATION_PRODUCT_KEYS.JPD,
				}),
			]);

			if (!confluenceResult.isEligible && !jpdResult.isEligible) {
				return;
			}

			const recommendations = await getMSRNudgeRecommendations();

			const nudgeKeys: string[] = [
				RECOMMENDATION_PRODUCT_KEYS.CONFLUENCE,
				RECOMMENDATION_PRODUCT_KEYS.JPD,
			];
			if (recommendations.filter((rec) => nudgeKeys.includes(rec)).length <= 0) {
				blockEligibilityUntilTomorrow();
				fireTrackAnalytics(createAnalyticsEvent({}), 'msrCjnEligibility blocked', {
					reason: 'no recommendations',
				});
				return;
			}

			for (const recommendation of recommendations) {
				switch (recommendation) {
					case RECOMMENDATION_PRODUCT_KEYS.JPD:
						if (jpdResult.isEligible) {
							snooze();
							return showJpdMsrCrossJoinNudge({ recommendations });
						}
						break;
					case RECOMMENDATION_PRODUCT_KEYS.CONFLUENCE:
						if (confluenceResult.isEligible) {
							snooze();
							return showMsrConfluenceCrossJoinNudge({ recommendations });
						}
						break;
					default:
				}
			}

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			if (error instanceof RecommendationError) {
				fireErrorAnalytics('recommendationMsrFetchFailed', error.options.cause, { cloudId });
			} else {
				fireErrorAnalytics('executeMSRNudge', error, { cloudId });
			}
			blockEligibilityUntilTomorrow();
			fireTrackAnalytics(createAnalyticsEvent({}), 'msrCjnEligibility blocked', {
				reason: 'unexpected error',
			});
		}
	}, [
		cloudId,
		getMsrJpdCrossJoinNudgeEligibility,
		getMsrConfluenceCrossJoinNudgeEligibility,
		getMSRNudgeRecommendations,
		blockEligibilityToPreventRace,
		blockEligibilityUntilTomorrow,
		showJpdMsrCrossJoinNudge,
		showMsrConfluenceCrossJoinNudge,
		withOverrides,
		snooze,
		fireOnlyNewEvent,
		createAnalyticsEvent,
	]);
};
